import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CardHeader,
  Button,
  TextField,
  Alert,
  Skeleton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import {
  getFirestore,
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  getCountFromServer
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import app from './firebase';
import { useWallet } from '@solana/wallet-adapter-react';
import { Transaction, PublicKey } from '@solana/web3.js';
import { getAssociatedTokenAddress, createTransferInstruction } from '@solana/spl-token';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import LazyPDFPage from './LazyPDFPage';
import { pdfjs } from 'react-pdf';
import { useBalance } from '../context/BalanceContext';

// PDF Worker Setup
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// -----------------------------------------------------
// Response Card for Uploaded Documents (includes vote button if type is "vote")
const ResponseCard = ({ response, rewardLabel }) => {
  const firestore = getFirestore(app);
  const auth = getAuth(app);
  const storage = getStorage(app);

  // State to track if the user has already voted
  const [userVoted, setUserVoted] = useState(false);

  // States related to PDF
  const [pdfUrl, setPdfUrl] = useState(null);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [numPages, setNumPages] = useState(0);

  // State for vote confirmation modal
  const [voteModalOpen, setVoteModalOpen] = useState(false);

  // State to track if voting is in progress
  const [isVoting, setIsVoting] = useState(false);

  // Check if the user has already voted (for UI display)
  useEffect(() => {
    if (!auth.currentUser) return;

    const userVoteQuery = query(
      collection(firestore, 'votes'),
      where('responseId', '==', response.id),
      where('userId', '==', auth.currentUser.uid)
    );

    getCountFromServer(userVoteQuery)
      .then((snapshot) => {
        setUserVoted(snapshot.data().count > 0);
      })
      .catch((error) => {
        console.error('Error checking user vote: ', error);
      });
  }, [auth.currentUser, response.id, firestore]);

  // Retrieve PDF download URL
  useEffect(() => {
    if (response.pdfFilePath) {
      const pdfRef = ref(storage, response.pdfFilePath);
      getDownloadURL(pdfRef)
        .then((url) => {
          setPdfUrl(url);
        })
        .catch((error) => {
          console.error('Error fetching PDF download URL:', error);
        });
    }
  }, [response.pdfFilePath, storage]);

  // Load the PDF viewer
  useEffect(() => {
    if (pdfUrl && showPdfViewer && !pdfDoc) {
      setIsPdfLoading(true);
      fetch(pdfUrl)
        .then((res) => res.blob())
        .then(async (blob) => {
          const arrayBuffer = await blob.arrayBuffer();
          const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
          setPdfDoc(pdf);
          setNumPages(pdf.numPages);
          setIsPdfLoading(false);
        })
        .catch((error) => {
          console.error("Error loading PDF document:", error);
          setIsPdfLoading(false);
        });
    }
  }, [pdfUrl, showPdfViewer, pdfDoc]);

  // Voting process
  const handleVote = async () => {
    if (!auth.currentUser) {
      alert('Please log in.');
      return;
    }
    // Set loading state when voting starts
    setIsVoting(true);
    try {
      const payload = {
        userId: auth.currentUser.uid,
        responseId: response.id
      };
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch('/vote', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) {
        console.error('Voting error:', data.error);
        alert('Failed to vote: ' + data.error);
        return;
      }
      // Upon successful vote, mark UI as voted
      setUserVoted(true);
      // Note: The votes_amount in the response object is incremented on the backend.
      // To reflect the latest value in the UI, you may need to refetch in the parent component or update state.
    } catch (error) {
      console.error('An error occurred during the voting process:', error);
      alert('An error occurred while voting.');
    } finally {
      // Remove the loading state after voting is complete
      setIsVoting(false);
    }
  };

  return (
    <Grid item xs={12} md={6}>
      <Card
        sx={{
          background: 'linear-gradient(135deg, #000, #1a1a1a)',
          padding: 2,
          borderRadius: 2,
          border: '1px solid #007acc',
          boxShadow: '0 0 10px #007acc',
          '&:hover': { boxShadow: '0 0 15px #007acc' }
        }}
      >
        <CardHeader
          title={
            <Typography variant="h6" sx={{ color: '#fff' }}>
              Address: {response.user_id}
            </Typography>
          }
          sx={{ paddingBottom: 0 }}
        />
        <CardContent>
          <Typography variant="body1" sx={{ color: '#fff', mb: 2 }}>
            {response.comment || 'No comment provided.'}
          </Typography>

          {/* PDF display toggle button */}
          {pdfUrl ? (
            <Button
              variant="contained"
              onClick={() => setShowPdfViewer((prev) => !prev)}
              sx={{
                mt: 1,
                background: 'linear-gradient(45deg, #007acc, #005999)',
                color: '#fff',
                '&:hover': { background: 'linear-gradient(45deg, #005999, #003f73)' }
              }}
            >
              {showPdfViewer ? 'Hide PDF' : 'Show PDF'}
            </Button>
          ) : (
            <Typography variant="body2" sx={{ color: '#bbb', mb: 2 }}>
              Loading PDF...
            </Typography>
          )}

          {showPdfViewer && (
            <Box sx={{ mt: 2 }}>
              {isPdfLoading ? (
                <Skeleton variant="rectangular" animation="wave" sx={{ width: '100%', height: 600 }} />
              ) : (
                <>
                  {pdfDoc ? (
                    Array.from({ length: numPages }, (_, index) => (
                      <LazyPDFPage key={index} pdf={pdfDoc} pageNumber={index + 1} scale={1} />
                    ))
                  ) : (
                    <Typography variant="body2" sx={{ color: '#fff' }}>
                      Failed to load PDF.
                    </Typography>
                  )}
                </>
              )}
            </Box>
          )}

          <Typography variant="caption" sx={{ color: '#bbb', display: 'block', mb: 1 }}>
            {response.userId} -{' '}
            {new Date(
              response.createdAt.seconds
                ? response.createdAt.seconds * 1000
                : response.createdAt
            ).toLocaleDateString()}
          </Typography>

          {response.status && (
            <Typography variant="caption" sx={{ color: '#f0ad4e', display: 'block', mb: 1 }}>
              Status: {response.status}
            </Typography>
          )}

          {/* Display the vote count from the response object */}
          {rewardLabel.toLowerCase() === 'vote' && (
            <>
              <Typography variant="caption" sx={{ color: '#fff', display: 'block', mb: 1 }}>
                Votes: {response.votes_amount}
              </Typography>
              <Button
                variant="contained"
                onClick={() => setVoteModalOpen(true)}
                disabled={userVoted || isVoting}  // Disabled if already voted or if voting is in progress
                sx={{
                  mt: 1,
                  background: userVoted || isVoting
                    ? '#666'
                    : 'linear-gradient(45deg, #007acc, #005999)',
                  color: '#fff',
                  '&:hover': {
                    background: userVoted || isVoting
                      ? '#666'
                      : 'linear-gradient(45deg, #005999, #003f73)'
                  }
                }}
              >
                {userVoted ? 'Voted' : 'Vote'}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Vote confirmation modal */}
      <Dialog
        open={voteModalOpen}
        onClose={() => setVoteModalOpen(false)}
        PaperProps={{ sx: { backgroundColor: '#000', border: '1px solid #007acc' } }}
      >
        <DialogTitle sx={{ color: '#fff' }}>Vote Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#fff' }}>
            By voting, you will earn 0.01 $EMCII. Do you wish to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoteModalOpen(false)} sx={{ color: '#007acc' }}>
            Cancel
          </Button>
          <Button
            onClick={async () => {
              await handleVote();
              setVoteModalOpen(false);
            }}
            disabled={isVoting}  // Disabled while voting is in progress
            sx={{ color: '#007acc' }}
            autoFocus
          >
            {isVoting ? 'Voting...' : 'Vote'}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

const OfficialRewardDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const firestore = getFirestore(app);
  const auth = getAuth(app);
  const { publicKey, signTransaction } = useWallet();
  const { tokenBalance } = useBalance();

  const [rewardData, setRewardData] = useState(null);
  const [loading, setLoading] = useState(true);

  // states for file upload (audit, vote)
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadComment, setUploadComment] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  // states for objective answer submission
  const [objectiveAnswer, setObjectiveAnswer] = useState('');
  const [objectiveError, setObjectiveError] = useState('');
  const [objectiveSubmitting, setObjectiveSubmitting] = useState(false);
  const [objectiveModalOpen, setObjectiveModalOpen] = useState(false);
  const [correctModalOpen, setCorrectModalOpen] = useState(false);
  const [incorrectModalOpen, setIncorrectModalOpen] = useState(false);
  const [hasAnsweredCorrectly, setHasAnsweredCorrectly] = useState(false);
  // responses list (for audit/vote only)
  const [responses, setResponses] = useState([]);
  const [responsesLoading, setResponsesLoading] = useState(false);

  // Fetch rewardData either from location.state or firestore
  useEffect(() => {
    if (location.state && location.state.rewardData) {
      setRewardData(location.state.rewardData);
      setLoading(false);
    } else {
      const fetchReward = async () => {
        try {
          const docRef = doc(firestore, 'official-rewards', id);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            setRewardData({ id: docSnap.id, ...docSnap.data() });
          } else {
            console.error('Official reward not found.');
          }
        } catch (error) {
          console.error('Error fetching official reward:', error);
        } finally {
          setLoading(false);
        }
      };
      fetchReward();
    }
  }, [firestore, id, location.state]);

  useEffect(() => {
    if (rewardData && rewardData.label.toLowerCase() === 'objective' && auth.currentUser) {
      const objectiveQuery = query(
        collection(firestore, 'objective-answers'),
        where('officialRewardId', '==', id),
        where('userId', '==', auth.currentUser.uid),
        where('isCorrect', '==', true)
      );
      getDocs(objectiveQuery).then((snapshot) => {
        if (!snapshot.empty) {
          setHasAnsweredCorrectly(true);
        }
      }).catch((error) => {
        console.error("Error checking objective answer status:", error);
      });
    }
  }, [rewardData, auth.currentUser, firestore, id]);

  // Fetch responses only for non-objective types
  const fetchResponses = async () => {
    if (!rewardData || rewardData.label.toLowerCase() === 'objective') return;
    setResponsesLoading(true);
    try {
      const responsesQuery = query(
        collection(firestore, 'rewards-requests'),
        where('officialRewardId', '==', id)
      );
      const snapshot = await getDocs(responsesQuery);
      const list = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));
      setResponses(list);
    } catch (error) {
      console.error('Error fetching responses:', error);
    } finally {
      setResponsesLoading(false);
    }
  };

  useEffect(() => {
    if (rewardData) {
      fetchResponses();
    }
  }, [rewardData]);

  // Handler for file selection (audit, vote)
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type !== 'application/pdf') {
      setUploadError('Only PDF files can be uploaded.');
      setUploadFile(null);
    } else {
      setUploadError('');
      setUploadFile(file);
    }
  };

  // Existing file upload submission (for audit, vote)
  const handleUploadSubmit = async () => {
    if (!uploadFile) {
      setUploadError('Please select a PDF file.');
      return;
    }
    if (!publicKey) {
      setUploadError('Please connect your wallet.');
      return;
    }
    setUploading(true);
    setUploadError('');
    try {
      // Blockchain processing
      setProcessingMessage('Creating transaction...');
      const receiverAddress = 'DbXmYN4bdAJKitknozxGww3YEVaegeRhWKkoYLzomJND';
      const mintAddress = 'Gc3eJ7fhAaJR2YsxyNs9nCb4HydBiLryKGxghW613sYK';
      const amount = 1;

      const senderTokenAddress = await getAssociatedTokenAddress(
        new PublicKey(mintAddress),
        publicKey
      );
      const receiverTokenAddress = await getAssociatedTokenAddress(
        new PublicKey(mintAddress),
        new PublicKey(receiverAddress)
      );

      setProcessingMessage('Fetching recent blockhash...');
      const blockhashResponse = await fetch('/recent_blockhash');
      if (!blockhashResponse.ok) {
        throw new Error('Failed to fetch recent blockhash from proxy');
      }
      const blockhashData = await blockhashResponse.json();
      if (!blockhashData || !blockhashData.result || !blockhashData.result.value) {
        throw new Error('Invalid blockhash response structure');
      }
      const recentBlockhash = blockhashData.result.value.blockhash;

      setProcessingMessage('Creating transaction instruction...');
      const transaction = new Transaction().add(
        createTransferInstruction(
          senderTokenAddress,
          receiverTokenAddress,
          publicKey,
          amount * 10 ** 9
        )
      );
      transaction.recentBlockhash = recentBlockhash;
      transaction.feePayer = publicKey;

      setProcessingMessage('Signing transaction...');
      const signedTx = await signTransaction(transaction);
      const serializedTx = signedTx.serialize().toString('base64');

      // Send data to server (including file upload details)
      setProcessingMessage('Sending data to server...');
      const formData = new FormData();
      formData.append('transaction', serializedTx);
      formData.append('title', rewardData.title);
      formData.append('user_id', auth.currentUser ? auth.currentUser.uid : publicKey.toBase58());
      formData.append('collection_name', 'rewards-requests');
      formData.append('comment', uploadComment);
      formData.append('officialRewardId', id);

      const idToken = await auth.currentUser.getIdToken();
      const submitResponse = await fetch('/submit', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${idToken}`
        },
        body: formData
      });
      const submitResult = await submitResponse.json();
      if (!submitResponse.ok) {
        throw new Error(submitResult.error);
      }

      // PDF file upload step
      setProcessingMessage('Uploading PDF...');
      const { uploadSignedURL } = submitResult;
      const uploadResponse = await fetch(uploadSignedURL, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/pdf'
        },
        body: uploadFile
      });
      if (!uploadResponse.ok) {
        throw new Error('Failed to upload PDF file');
      }

      setProcessingMessage('Upload complete! Refreshing responses...');
      await fetchResponses();

      setUploadFile(null);
      setUploadComment('');
      setProcessingMessage('');
    } catch (error) {
      console.error('Error during submission:', error);
      setUploadError('Submission error: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  // New handler for objective answer submission
  const handleObjectiveSubmit = async () => {
    if (!objectiveAnswer) {
      setObjectiveError('Please enter your answer.');
      return;
    }
    if (!publicKey) {
      setObjectiveError('Please connect your wallet.');
      return;
    }
    setObjectiveSubmitting(true);
    setObjectiveError('');
    try {
      // Blockchain processing (same as before)
      setProcessingMessage('Creating transaction...');
      const receiverAddress = 'DbXmYN4bdAJKitknozxGww3YEVaegeRhWKkoYLzomJND';
      const mintAddress = 'Gc3eJ7fhAaJR2YsxyNs9nCb4HydBiLryKGxghW613sYK';
      const amount = 1;

      const senderTokenAddress = await getAssociatedTokenAddress(
        new PublicKey(mintAddress),
        publicKey
      );
      const receiverTokenAddress = await getAssociatedTokenAddress(
        new PublicKey(mintAddress),
        new PublicKey(receiverAddress)
      );

      setProcessingMessage('Fetching recent blockhash...');
      const blockhashResponse = await fetch('/recent_blockhash');
      if (!blockhashResponse.ok) {
        throw new Error('Failed to fetch recent blockhash from proxy');
      }
      const blockhashData = await blockhashResponse.json();
      if (!blockhashData || !blockhashData.result || !blockhashData.result.value) {
        throw new Error('Invalid blockhash response structure');
      }
      const recentBlockhash = blockhashData.result.value.blockhash;

      setProcessingMessage('Creating transaction instruction...');
      const transaction = new Transaction().add(
        createTransferInstruction(
          senderTokenAddress,
          receiverTokenAddress,
          publicKey,
          amount * 10 ** 9
        )
      );
      transaction.recentBlockhash = recentBlockhash;
      transaction.feePayer = publicKey;

      setProcessingMessage('Signing transaction...');
      const signedTx = await signTransaction(transaction);
      const serializedTx = signedTx.serialize().toString('base64');

      // Send data to server for objective answer
      setProcessingMessage('Sending answer to server...');
      const formData = new FormData();
      formData.append('transaction', serializedTx);
      formData.append('title', rewardData.title);
      formData.append('user_id', auth.currentUser ? auth.currentUser.uid : publicKey.toBase58());
      // collection_name can be set as needed; here we use a dedicated one for objective answers
      formData.append('collection_name', 'objective-answers');
      formData.append('answer', objectiveAnswer);
      formData.append('officialRewardId', id);

      const idToken = await auth.currentUser.getIdToken();
      const submitResponse = await fetch('/submit_objective', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${idToken}`
        },
        body: formData
      });
      const submitResult = await submitResponse.json();
      if (!submitResponse.ok) {
        throw new Error(submitResult.error);
      }
      // サーバ側で正解・不正解が判定され、submitResult に isCorrect などのフラグが返ってくる想定
      if (submitResult.isCorrect) {
        setCorrectModalOpen(true);
      } else {
        setIncorrectModalOpen(true);
      }
      setProcessingMessage('');
    } catch (error) {
      console.error('Error during objective submission:', error);
      setObjectiveError('Submission error: ' + error.message);
    } finally {
      setObjectiveSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', padding: 4, color: '#fff' }}>
        <Skeleton variant="rectangular" width="100%" height={100} animation="wave" sx={{ mb: 2 }} />
        <Skeleton variant="text" width="60%" animation="wave" sx={{ mb: 1 }} />
        <Skeleton variant="text" width="80%" animation="wave" sx={{ mb: 1 }} />
        <Skeleton variant="rectangular" width="100%" height={300} animation="wave" />
      </Box>
    );
  }
  if (!rewardData) {
    return (
      <Box sx={{ minHeight: '100vh', padding: 4, color: '#fff' }}>
        <Typography variant="h6">Official reward not found.</Typography>
      </Box>
    );
  }

  // isUploadEnabled now includes objective as well
  const isUploadEnabled =
    rewardData.label &&
    ['audit', 'vote', 'objective'].includes(rewardData.label.toLowerCase());

  return (
    <>
      <Helmet>
        <title>{rewardData.title}</title>
        <meta property="og:title" content={rewardData.title} />
        <meta property="og:description" content={rewardData.desc} />
      </Helmet>

      <Box
        sx={{
          padding: 4,
          color: '#fff',
          background: 'linear-gradient(135deg, #000, #0d0d0d)',
          minHeight: '100vh'
        }}
      >
        <Grid container spacing={4}>
          {/* Official Reward Details */}
          <Grid item xs={12}>
            <Button
              variant="contained"
              onClick={() => navigate("/official-rewards")}
              sx={{
                background: 'linear-gradient(45deg, #007acc, #005999)',
                color: '#fff',
                mb: 2
              }}
            >
              ← Official Rewards
            </Button>
            <Card
              sx={{
                background: 'linear-gradient(135deg, #000, #1a1a1a)',
                padding: 2,
                border: '1px solid #007acc',
                boxShadow: '0 0 10px #007acc'
              }}
            >
              <CardContent>
                {rewardData.thumbnail && (
                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <img
                      src={rewardData.thumbnail}
                      alt="Thumbnail"
                      style={{ maxWidth: '100%', borderRadius: '8px' }}
                    />
                  </Box>
                )}
                <Typography variant="h4" sx={{ color: '#90caf9', fontFamily: 'Orbitron, sans-serif' }}>
                  {rewardData.title}
                </Typography>
                {rewardData.label && (
                  <Typography
                    variant="caption"
                    sx={{
                      backgroundColor:
                        rewardData.label.toLowerCase() === 'audit'
                          ? 'rgba(244, 143, 177, 0.7)'
                          : rewardData.label.toLowerCase() === 'vote'
                          ? 'rgba(102, 187, 106, 0.7)'
                          : rewardData.label.toLowerCase() === 'objective'
                          ? 'rgba(100, 149, 237, 0.7)'
                          : 'rgba(0, 0, 0, 0.5)',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      color: '#fff',
                      fontFamily: 'Orbitron, sans-serif',
                      display: 'inline-block',
                      mb: 1
                    }}
                  >
                    {rewardData.label}
                  </Typography>
                )}
                <Typography variant="body1" sx={{ color: '#bbb', fontFamily: 'Orbitron, sans-serif' }}>
                  {rewardData.desc}
                </Typography>
                <Typography variant="body2" sx={{ color: '#bbb', mt: 1 }}>
                  Reward: {new Intl.NumberFormat('en-US').format(rewardData.reward)} $EMCII
                </Typography>
                {rewardData.expiration && (
                  <Typography variant="body2" sx={{ color: '#bbb', mt: 1 }}>
                    Expiration:{' '}
                    {rewardData.expiration.seconds
                      ? new Date(rewardData.expiration.seconds * 1000).toLocaleDateString()
                      : new Date(rewardData.expiration).toLocaleDateString()}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Upload / Answer Form */}
          {isUploadEnabled && (
  <>
    {rewardData.label.toLowerCase() === 'objective' ? (
      hasAnsweredCorrectly ? (
        // すでに正解済みの場合のUI
        <Grid item xs={12}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #000, #1a1a1a)',
              padding: 2,
              border: '1px solid #007acc',
              boxShadow: '0 0 10px #007acc'
            }}
          >
            <CardContent>
              <Typography variant="h5" sx={{ color: '#fff', mb: 2 }}>
                You've already answer.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ) : (
        // Objective 回答未提出の場合のUI
        <Grid item xs={12}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #000, #1a1a1a)',
              padding: 2,
              border: '1px solid #007acc',
              boxShadow: '0 0 10px #007acc'
            }}
          >
            <CardContent>
              <Typography variant="h5" sx={{ color: '#fff', mb: 2 }}>
                Submit Your Answer (1 $EMCII fee)
              </Typography>
              <Typography variant="body2" sx={{ color: '#bbb', mb: 1 }}>
                Current EMCII Balance: {tokenBalance} EMCII
              </Typography>
              <TextField
                label="Your Answer"
                variant="outlined"
                fullWidth
                multiline
                rows={3}
                value={objectiveAnswer}
                onChange={(e) => setObjectiveAnswer(e.target.value)}
                sx={{
                  mt: 2,
                  backgroundColor: '#333',
                  input: { color: '#fff' }
                }}
              />
              {objectiveError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {objectiveError}
                </Alert>
              )}
              <Button
                variant="contained"
                onClick={() => {
                  if (tokenBalance < 1) {
                    setObjectiveError('Insufficient EMCII balance.');
                    return;
                  }
                  if (!objectiveAnswer) {
                    setObjectiveError('Please enter your answer.');
                    return;
                  }
                  if (!publicKey) {
                    setObjectiveError('Please connect your wallet.');
                    return;
                  }
                  setObjectiveModalOpen(true);
                }}
                sx={{
                  background: 'linear-gradient(45deg, #007acc, #005999)',
                  color: '#fff',
                  mt: 2
                }}
                disabled={objectiveSubmitting || tokenBalance < 1}
              >
                {objectiveSubmitting ? processingMessage || 'Submitting...' : 'Submit Answer'}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      )
    ) : (
      // Audit, Vote の場合の既存ファイルアップロードUI
      <Grid item xs={12}>
        <Card
          sx={{
            background: 'linear-gradient(135deg, #000, #1a1a1a)',
            padding: 2,
            border: '1px solid #007acc',
            boxShadow: '0 0 10px #007acc'
          }}
        >
          <CardContent>
            <Typography variant="h5" sx={{ color: '#fff', mb: 2 }}>
              Upload Your Idea (1 $EMCII for upload fee)
            </Typography>
            <Typography variant="body2" sx={{ color: '#bbb', mb: 1 }}>
              Current EMCII Balance: {tokenBalance} EMCII
            </Typography>
            <Box component="form" noValidate>
              <Button
                variant="contained"
                component="label"
                sx={{
                  background: 'linear-gradient(45deg, #007acc, #005999)',
                  color: '#fff'
                }}
              >
                Select PDF
                <input type="file" accept="application/pdf" hidden onChange={handleFileChange} />
              </Button>
              {uploadFile && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selected file: {uploadFile.name}
                </Typography>
              )}
              <TextField
                label="Comment (optional)"
                variant="outlined"
                fullWidth
                multiline
                rows={3}
                value={uploadComment}
                onChange={(e) => setUploadComment(e.target.value)}
                sx={{
                  mt: 2,
                  backgroundColor: '#333',
                  input: { color: '#fff' }
                }}
              />
              {uploadError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {uploadError}
                </Alert>
              )}
              <Button
                variant="contained"
                onClick={() => {
                  if (tokenBalance < 1) {
                    setUploadError('Insufficient EMCII balance.');
                    return;
                  }
                  if (!uploadFile) {
                    setUploadError('Please select a PDF file.');
                    return;
                  }
                  if (!publicKey) {
                    setUploadError('Please connect your wallet.');
                    return;
                  }
                  setUploadModalOpen(true);
                }}
                sx={{
                  background: 'linear-gradient(45deg, #007acc, #005999)',
                  color: '#fff',
                  mt: 2
                }}
                disabled={uploading || tokenBalance < 1}
              >
                {uploading ? processingMessage || 'Uploading...' : 'Upload'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    )}
  </>
)}


          {/* Confirmation Modal for file upload */}
          <Dialog
            open={uploadModalOpen}
            onClose={() => setUploadModalOpen(false)}
            PaperProps={{ sx: { backgroundColor: '#000', border: '1px solid #007acc' } }}
          >
            <DialogTitle sx={{ color: '#fff' }}>Upload Confirmation</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ color: '#fff' }}>
                Uploading will consume 1 $EMCII, and if your response gets votes, you'll earn 0.5 $EMCII.
                Do you want to proceed?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setUploadModalOpen(false)} sx={{ color: '#007acc' }}>
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  await handleUploadSubmit();
                  setUploadModalOpen(false);
                }}
                sx={{ color: '#007acc' }}
                autoFocus
              >
                Confirm Upload
              </Button>
            </DialogActions>
          </Dialog>

          {/* Confirmation Modal for objective answer submission */}
          <Dialog
            open={objectiveModalOpen}
            onClose={() => setObjectiveModalOpen(false)}
            PaperProps={{ sx: { backgroundColor: '#000', border: '1px solid #007acc' } }}
          >
            <DialogTitle sx={{ color: '#fff' }}>Submit Answer Confirmation</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ color: '#fff' }}>
                Submitting your answer will consume 1 $EMCII fee. Do you wish to proceed?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setObjectiveModalOpen(false)} sx={{ color: '#007acc' }}>
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  await handleObjectiveSubmit();
                  setObjectiveModalOpen(false);
                }}
                sx={{ color: '#007acc' }}
                autoFocus
              >
                Confirm
              </Button>
            </DialogActions>
          </Dialog>

          {/* Correct Answer Modal */}
          <Dialog
            open={correctModalOpen}
            onClose={() => setCorrectModalOpen(false)}
            PaperProps={{ sx: { backgroundColor: '#000', border: '1px solid #007acc' } }}
          >
            <DialogTitle sx={{ color: '#fff' }}>Correct Answer!</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ color: '#fff' }}>
                Congratulations! Your answer is correct.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setCorrectModalOpen(false)} sx={{ color: '#007acc' }}>
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* Incorrect Answer Modal */}
          <Dialog
            open={incorrectModalOpen}
            onClose={() => setIncorrectModalOpen(false)}
            PaperProps={{ sx: { backgroundColor: '#000', border: '1px solid #007acc' } }}
          >
            <DialogTitle sx={{ color: '#fff' }}>Incorrect Answer</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ color: '#fff' }}>
                Unfortunately, your answer is incorrect. Please try again.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setIncorrectModalOpen(false)} sx={{ color: '#007acc' }}>
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* Display Responses List only for non-objective types */}
          {rewardData.label.toLowerCase() !== 'objective' && (
            <Grid item xs={12}>
              <Typography variant="h5" sx={{ color: '#fff', mb: 2 }}>
                Community Ideas
              </Typography>
              {responses.length === 0 ? (
                <Typography variant="body1" sx={{ color: '#bbb', ml: 2 }}>
                  No ideas yet.
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {/* Assuming ResponseCard is defined elsewhere */}
                  {responses.map((response) => (
                    <ResponseCard key={response.id} response={response} rewardLabel={rewardData.label} />
                  ))}
                </Grid>
              )}
            </Grid>
          )}
        </Grid>
      </Box>
    </>
  );
};

export default OfficialRewardDetail;
